import { RetryLink } from '@apollo/client/link/retry';
import { Kind, OperationTypeNode } from 'graphql';
import { isTransientNetworkFailure } from './is-transient-network-failure';
const isQuery = (operation) => {
    return operation.query.definitions
        .filter(definition => definition.kind === Kind.OPERATION_DEFINITION)
        .every(definition => definition.operation === OperationTypeNode.QUERY);
};
/**
 * Creates a retry link for handling network failures.
 * Retries failed requests with exponential backoff.
 *
 * Configuration:
 * - attempts.max: Maximum number of requests, the initial one included (3 = 1 request + 2 retries)
 * - delay: Uses exponential backoff starting at 300ms, capped at 3000ms with jitter to prevent thundering herd
 * - retryIf: Only retries on transient network errors (connection failures, timeouts, DNS failures),
 *   not on GraphQL errors, server errors with response bodies, or authentication errors
 *
 * @returns RetryLink instance configured for network resilience
 */
export const createRetryLink = () => new RetryLink({
    delay: {
        initial: 300,
        max: 3000,
        jitter: true,
    },
    attempts: {
        max: 3,
        retryIf: (error, operation) => {
            // We only want to retry queries, as we cannot reliably know whether a mutation
            // reached the server.
            if (!isQuery(operation)) {
                return false;
            }
            // Only retry on transient network failures (connection errors, timeouts, DNS failures)
            // Don't retry:
            // - GraphQL errors (application-level validation or business logic errors)
            // - Server errors with response bodies (4xx, 5xx with parsed content)
            // - Authentication errors (handled separately by authLink)
            return isTransientNetworkFailure(error);
        },
    },
});
